import { useAuth } from "@elit-hair/elit-tools";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { callSvcAxiosInstance } from "@/constants";
import { callServiceEndpoints } from "@/constants/endpoints";
import { createQueryKey } from "@/constants/query-keys";
import {
    ApiPhoneNumber,
    AvailablePhoneNumber,
    CreatePhoneNumberPayload,
    IPhoneNumber,
    PhoneNumberStatus,
} from "@/types/phone.types";

const mapApiPhoneNumbers = (data: ApiPhoneNumber[]): IPhoneNumber[] => {
    if (!Array.isArray(data)) {
        console.error("API response is not an array");
        return [];
    }

    return data
        .map((item: ApiPhoneNumber) => {
            if (!item || typeof item !== "object") {
                return null;
            }

            try {
                const phoneNumber: IPhoneNumber = {
                    id: item.id || "",
                    number: item.phoneNumber,
                    isActive: item.status === PhoneNumberStatus.ACTIVE,
                    name: item.name,
                    countries: item.countries,
                    brands: item.brands,
                };

                return phoneNumber;
            } catch (err) {
                console.error(`Error processing phone number:`, err);
                return null;
            }
        })
        .filter((item): item is IPhoneNumber => item !== null);
};

const getPhoneNumbers = async (): Promise<IPhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getPhoneNumbers);
    return mapApiPhoneNumbers(response.data);
};

const getMyPhoneNumbers = async (): Promise<IPhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getMyPhoneNumbers);
    return mapApiPhoneNumbers(response.data);
};

const getAvailablePhoneNumbers = async (): Promise<AvailablePhoneNumber[]> => {
    const response = await callSvcAxiosInstance.get(callServiceEndpoints.getAvailablePhoneNumbers);

    if (!Array.isArray(response.data)) {
        console.error("API response is not an array");
        return [];
    }

    return response.data as AvailablePhoneNumber[];
};

const createPhoneNumber = async (data: CreatePhoneNumberPayload): Promise<ApiPhoneNumber> => {
    const response = await callSvcAxiosInstance.post(callServiceEndpoints.createPhoneNumber, data);
    return response.data as ApiPhoneNumber;
};

const updatePhoneNumber = async ({
    id,
    data,
}: {
    id: string;
    data: CreatePhoneNumberPayload;
}): Promise<ApiPhoneNumber> => {
    const response = await callSvcAxiosInstance.patch(callServiceEndpoints.updatePhoneNumber(id), data);
    return response.data as ApiPhoneNumber;
};

const deletePhoneNumber = async (id: string): Promise<void> => {
    await callSvcAxiosInstance.delete(callServiceEndpoints.deletePhoneNumber(id));
};

export const usePhoneNumbers = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.phoneNumbers(),
        queryFn: getPhoneNumbers,
        enabled: !!auth, // Only run when authenticated
    });
};

export const useMyPhoneNumbers = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.myPhoneNumbers(),
        queryFn: getMyPhoneNumbers,
        enabled: !!auth,
    });
};

export const useAvailablePhoneNumbers = () => {
    const { auth } = useAuth();

    return useQuery({
        queryKey: createQueryKey.availablePhoneNumbers(),
        queryFn: getAvailablePhoneNumbers,
        enabled: !!auth, // Only run when authenticated
    });
};

const invalidatePhoneNumberQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
    queryClient.invalidateQueries({ queryKey: createQueryKey.phoneNumbers() });
    queryClient.invalidateQueries({ queryKey: createQueryKey.myPhoneNumbers() });
};

export const useCreatePhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createPhoneNumber,
        onSuccess: () => {
            invalidatePhoneNumberQueries(queryClient);
        },
    });
};

export const useUpdatePhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: updatePhoneNumber,
        onSuccess: () => {
            invalidatePhoneNumberQueries(queryClient);
        },
    });
};

export const useDeletePhoneNumber = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deletePhoneNumber,
        onSuccess: () => {
            invalidatePhoneNumberQueries(queryClient);
        },
    });
};
